import { useCallback, useState, useEffect } from "react"

import { useRequest } from "@dnr/hooks"
import { useTranslate } from "@dnr/localization"
import { productsCategoryService } from "@dnr/data/services"
import { IFilter } from "@dnr/data/models"
import { Dropdown, DropdownOption } from "@dnr/ui/dropdowns"

export interface ProductCategoriesDropdownProps {
    reset: number
    selectedId?: number
    parentCategoryId?: number
    onUpdate: (selectedId: number) => void
    placeholder?: string
    color?: string
    isFilter?: boolean
    onReset?: () => void
}

export const ProductCategoriesDropdown = (props: ProductCategoriesDropdownProps) => {
    const { t } = useTranslate()

    const [productCategoriesOptions, setProductCategoriesOptions] = useState<DropdownOption[]>([])
    const [selectedDisplayText, setDisplayText] = useState<string>(
        props?.placeholder ? props.placeholder : t.common("PRODUCT.SELECT_CATEGORY")
    )
    const [displayChildrenOfId, setDisplayChildrenOfId] = useState<number>()
    const [categoriesWithChildren, setCategoriesWithChildren] = useState<number[]>([])

    useEffect(() => {
        return setDisplayChildrenOfId(undefined)
    }, [props.reset])

    useEffect(() => {
        if (props.placeholder) {
            setDisplayText(props.placeholder)
        }
    }, [props.placeholder])

    const { loading } = useRequest(
        async (options) => {
            const { result: categoriesResponse } = await productsCategoryService.find(
                {
                    pageNumber: 1,
                    pageSize: 100,
                    parentCategoryId: props.parentCategoryId,
                } as IFilter,
                {
                    abortController: options.abortController,
                }
            )

            const categories = categoriesResponse.items
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map((item) => {
                    return {
                        key: item.categoryId.toString(),
                        value: item.name,
                        displayText: item.displayName,
                    } as DropdownOption
                })

            setCategoriesWithChildren(categoriesResponse.items.filter((c) => !c.isLeaf).map((c) => c.categoryId))
            setProductCategoriesOptions(categories)
        },
        [props.parentCategoryId]
    )

    const generateChildCategoryDropdown = useCallback(() => {
        return (
            <div className="u-mt--sml--2">
                <ProductCategoriesDropdown
                    reset={props.reset}
                    key={displayChildrenOfId}
                    onUpdate={(id) => props.onUpdate(id)}
                    parentCategoryId={displayChildrenOfId}
                    placeholder={t.common("PRODUCT.SELECT_SUBCATEGORY")}
                    color="light"
                />
            </div>
        )
    }, [displayChildrenOfId])

    return (
        <div>
            {loading ? (
                "Loading"
            ) : (
                <div>
                    <Dropdown
                        resetStates={props.reset}
                        id="category-dropdown"
                        label=""
                        type="default"
                        color={props.color}
                        placeholder={selectedDisplayText}
                        selectedValue={props.selectedId?.toString()}
                        onChange={(option) => {
                            const selectedId = Number(option.key)
                            if (categoriesWithChildren.includes(selectedId)) {
                                setDisplayChildrenOfId(selectedId)
                            } else {
                                setDisplayChildrenOfId(undefined)
                            }
                            props.onUpdate(Number(option.key))
                        }}
                        options={productCategoriesOptions}
                        isFilter={props.isFilter}
                        resetFilter={props.onReset}
                    />
                    <div>
                        {displayChildrenOfId !== undefined && displayChildrenOfId !== 1
                            ? generateChildCategoryDropdown()
                            : ""}
                    </div>
                </div>
            )}
        </div>
    )
}
